import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import type { GiftCardSettingsSection } from '@/components/gift-cards/settings/gift-card-settings-sidebar';
import { ArtworkSection } from '@/components/gift-cards/settings/artwork-section';
import { GiftCardSettingsSidebar } from '@/components/gift-cards/settings/gift-card-settings-sidebar';
import { GiftCardSettingsMobileNav } from '@/components/gift-cards/settings/gift-card-settings-mobile-nav';
import { OnlineSalesSection } from '@/components/gift-cards/settings/online-sales-section';
import { PreferencesSection } from '@/components/gift-cards/settings/preferences-section';
import { PromotionsSection } from '@/components/gift-cards/settings/promotions-section';
import { useCanManageSalonAdminEquivalent } from '@/providers';
import { PageCanvas } from '@/components/layout/page-canvas';
import { PAGE_CONTENT_WIDTH_CLASS } from '@/components/layout/page-content-width';
import { formatDocumentTitle } from '@/lib/document-title';
import { BREAKPOINT_QUERY } from '@/hooks/breakpoint-queries';
import { useMediaQuery } from '@/hooks/use-media-query';

/**
 * Gift Card Settings — reached from the Gift Cards page "Settings" button.
 * Styled to match the main Settings pages: fixed left sidebar + left-aligned
 * scrollable content for settings sections.
 *
 * The trailing `_` in `gift-cards_` opts this route out of nesting under
 * `gift-cards`, so it renders as its own full page at
 * `/$orgId/gift-cards/settings`.
 */
type GiftCardSettingsSearchParams = {
  section?: GiftCardSettingsSection;
};

const VALID_GIFT_CARD_SETTINGS_SECTIONS = new Set<GiftCardSettingsSection>([
  'online-sales',
  'artwork',
  'preferences',
  'promotions',
]);

export const Route = createFileRoute('/_auth/$orgId/gift-cards_/settings')({
  validateSearch: (search: Record<string, unknown>): GiftCardSettingsSearchParams => ({
    section:
      typeof search.section === 'string' &&
      VALID_GIFT_CARD_SETTINGS_SECTIONS.has(search.section as GiftCardSettingsSection)
        ? (search.section as GiftCardSettingsSection)
        : undefined,
  }),
  component: GiftCardSettingsPage,
});

function GiftCardSettingsPage() {
  const allowed = useCanManageSalonAdminEquivalent();
  return allowed ? <AuthorizedGiftCardSettingsPage /> : <p className="p-6 text-clovie-muted">Gift card settings require administrator access.</p>;
}

function AuthorizedGiftCardSettingsPage() {
  const { orgId } = useParams({ from: '/_auth/$orgId/gift-cards_/settings' });
  const navigate = useNavigate({ from: '/$orgId/gift-cards/settings' });
  const canWriteOperational = useCanManageSalonAdminEquivalent();
  const { section } = Route.useSearch();
  const activeSection = section ?? 'online-sales';
  const readOnly = !canWriteOperational;
  // Desktop: section sidebar beside the content. Phone/tablet: back row + chip row above it.
  const isDesktop = useMediaQuery(BREAKPOINT_QUERY.lgUp);

  const handleSelectSection = (nextSection: GiftCardSettingsSection) => {
    navigate({
      search: (previous) => ({
        ...previous,
        section: nextSection === 'online-sales' ? undefined : nextSection,
      }),
    });
  };

  const renderSection =
    activeSection === 'artwork' ? (
      <ArtworkSection orgId={orgId} readOnly={readOnly} />
    ) : activeSection === 'preferences' ? (
      <PreferencesSection orgId={orgId} readOnly={readOnly} />
    ) : activeSection === 'promotions' ? (
      <PromotionsSection readOnly={readOnly} />
    ) : (
      <OnlineSalesSection />
    );

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Gift Card Settings')}</title>
      </Helmet>

      <PageCanvas className="box-border h-full min-h-0 overflow-hidden">
        <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface shadow-soft-lg max-sm:rounded-none max-sm:border-0 max-sm:shadow-none lg:flex-row">
          {isDesktop ? (
            <GiftCardSettingsSidebar
              orgId={orgId}
              active={activeSection}
              onSelect={handleSelectSection}
            />
          ) : (
            <GiftCardSettingsMobileNav
              orgId={orgId}
              active={activeSection}
              onSelect={handleSelectSection}
            />
          )}

          <main className="min-w-0 flex-1 overflow-y-auto bg-clovie-surface-2 px-4 py-6 [overflow-anchor:none] sm:px-6 lg:px-8 lg:py-8">
            <div className={PAGE_CONTENT_WIDTH_CLASS.form}>{renderSection}</div>
          </main>
        </div>
      </PageCanvas>
    </>
  );
}
